import { useEffect, useState } from "react";
import styled from "styled-components";
import { AccordionBody, AccordionHeader, AccordionList, AccordionListItem } from "../../components/utilities";
import { toNString } from "../../helpers/guids";
import { useClientContext } from "../../hooks/ClientContext";
import { ExpenditureDetailsProvider } from "./contexts/ExpenditureDetailsContext";
import { IncomeDetailsProvider } from "./contexts/IncomeDetailsContext";
import { withTooltipProvider } from "./contexts/TooltipContext";
import {
    AgreedVolatilityProfileSection,
    AntiMoneyLaunderingAccordion,
    AssetsSection,
    BackgroundSection,
    EstatePlanningSection,
    ExpenditureDetailsSection,
    IncomeDetailsSection,
    InvestmentsSection,
    LoansAndLiabilitiesSection,
    NewMortgageApplicationSection,
    PersonalDetailsSection,
    ProtectionSection,
    RiskFactorsSection
} from "./sections";
import VulnerabilitiesSection from "./sections/VulnerabilitiesSection";

// NewMortgageApplication needs to be at the bottom,
// and Assets and LoansAndLiabilities need to be immediately after ExpenditureDetails
const CustomPlacementAccordion = styled(AccordionListItem)`
    grid-area: ${props => props.listIndex};
`;

const AssetsIndex = 7;
const LoansAndLiabilitiesIndex = 8;
const MortgageApplicationIndex = 14;

export const FactFindSections = {
    Background: 10,
    PersonalDetails: 20,
    Income: 30,
    Expenditure: 40,
    InvestmentsAndPensions: 50,
    RiskFactors: 60,
    AgreedVolatilityProfile: 70,
    EstatePlanning: 80,
    Protection: 90,
    LoansAndLiabilities: 100,
    Assets: 110,
    NewMortgageApplication: 120,
    Notes: 130,
    AntiMoneyLaundering: 140,
    Vulnerability: 150
};

const ClientFactFind = () => {

    const { id: masterAccountId } = useClientContext();

    // Track opened FF accordions in master-account-specific session storage
    const sectionStorageKey = `${toNString(masterAccountId)}-fact-find-sections`;
    const [activeKeys, setActiveKeys] = useState(JSON.parse(sessionStorage.getItem(sectionStorageKey)) ?? []);

    useEffect(() => {
        sessionStorage.setItem(sectionStorageKey, JSON.stringify(activeKeys));
    }, [activeKeys]);

    return <div className="p-3">
        <AccordionList
            className="d-grid"
            activeKey={activeKeys}
            defaultActiveKey={null}
            onSelect={(eventKeys, _) => setActiveKeys(eventKeys)}
            gap={1}
            alwaysOpen
        >
            <AccordionListItem key={"antimoneylaundering"} eventKey={"antimoneylaundering"}>
                <AntiMoneyLaunderingAccordion renderBody={activeKeys.includes("antimoneylaundering")} />
            </AccordionListItem>

            <AccordionListItem key={"vulnerability"} eventKey={"vulnerability"}>
                <AccordionHeader>
                    <span>Client Vulnerabilities</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("vulnerability") && <VulnerabilitiesSection />}
                </AccordionBody>
            </AccordionListItem>

            <AccordionListItem key={"background"} eventKey={"background"}>
                <AccordionHeader>
                    <span>Background</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("background") && <BackgroundSection />}
                </AccordionBody>
            </AccordionListItem>

            <AccordionListItem key={"personaldetails"} eventKey={"personaldetails"} >
                <AccordionHeader>
                    <span>Personal Details, Health, and Family</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("personaldetails") && <PersonalDetailsSection />}
                </AccordionBody>
            </AccordionListItem>

            <IncomeDetailsProvider>
                <AccordionListItem key={"income"} eventKey={"income"}>
                    <AccordionHeader>
                        Income Details
                    </AccordionHeader>
                    <AccordionBody>
                        <IncomeDetailsSection />
                    </AccordionBody>
                </AccordionListItem>

                <ExpenditureDetailsProvider>
                    <AccordionListItem key={"expenditure"} eventKey={"expenditure"}>
                        <AccordionHeader>
                            Expenditure Details
                        </AccordionHeader>
                        <AccordionBody>
                            <ExpenditureDetailsSection />
                        </AccordionBody>
                    </AccordionListItem>

                    <CustomPlacementAccordion key={"mortgage"} eventKey={"mortgage"} listIndex={MortgageApplicationIndex}>
                        <AccordionHeader>
                            <span>New Mortgage Application</span>
                        </AccordionHeader>
                        <AccordionBody>
                            {activeKeys.includes("mortgage") && <NewMortgageApplicationSection />}
                        </AccordionBody>
                    </CustomPlacementAccordion>

                </ExpenditureDetailsProvider>
            </IncomeDetailsProvider>

            <CustomPlacementAccordion key={"assets"} eventKey={"assets"} listIndex={AssetsIndex}>
                <AccordionHeader>
                    <span>Assets</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("assets") && <AssetsSection />}
                </AccordionBody>
            </CustomPlacementAccordion>

            <CustomPlacementAccordion key={"liabilities"} eventKey={"liabilities"} listIndex={LoansAndLiabilitiesIndex}>
                <AccordionHeader>
                    <span>Loans & Other Liabilities</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("liabilities") && <LoansAndLiabilitiesSection />}
                </AccordionBody>
            </CustomPlacementAccordion>

            <AccordionListItem key={"investments"} eventKey={"investments"}>
                <AccordionHeader>
                    <span>Investments & Pensions</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("investments") && <InvestmentsSection />}
                </AccordionBody>
            </AccordionListItem>

            <AccordionListItem key={"riskfactors"} eventKey={"riskfactors"}>
                <AccordionHeader>
                    <span>Risk Factors</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("riskfactors") && <RiskFactorsSection />}
                </AccordionBody>
            </AccordionListItem>

            <AccordionListItem key={"volatilityprofile"} eventKey={"volatilityprofile"}>
                <AccordionHeader>
                    <span>Agreed Volatility Profile</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("volatilityprofile") && <AgreedVolatilityProfileSection />}
                </AccordionBody>
            </AccordionListItem>

            <AccordionListItem key={"estateplanning"} eventKey={"estateplanning"}>
                <AccordionHeader>
                    <span>Estate Planning</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("estateplanning") && <EstatePlanningSection />}
                </AccordionBody>
            </AccordionListItem>

            <AccordionListItem key={"protection"} eventKey={"protection"}>
                <AccordionHeader>
                    <span>Protection</span>
                </AccordionHeader>
                <AccordionBody>
                    {activeKeys.includes("protection") && <ProtectionSection />}
                </AccordionBody>
            </AccordionListItem>
        </AccordionList>
    </div>;
}

export default withTooltipProvider(ClientFactFind);